import { useState, useRef, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useInView } from "framer-motion";
import skinBefore from "@/assets/skin-before.jpg";
import skinAfter from "@/assets/skin-after.jpg";

export function BeforeAfterSlider() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.5 });

  // Auto-slide animation when component comes into view
  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
      
      const animate = async () => {
        // Wait a moment before starting
        await new Promise(r => setTimeout(r, 400));
        
        // Slide to reveal more "before" (left)
        const slideLeft = () => {
          let pos = 50;
          const interval = setInterval(() => {
            pos -= 2;
            if (pos <= 30) {
              clearInterval(interval);
              slideRight();
            }
            setSliderPosition(pos);
          }, 20);
        };
        
        // Slide to reveal more "after" (right)
        const slideRight = () => {
          let pos = 30;
          const interval = setInterval(() => {
            pos += 2;
            if (pos >= 70) {
              clearInterval(interval);
              slideCenter();
            }
            setSliderPosition(pos);
          }, 20);
        };
        
        // Return to center
        const slideCenter = () => {
          let pos = 70;
          const interval = setInterval(() => {
            pos -= 2;
            if (pos <= 50) {
              clearInterval(interval);
              setSliderPosition(50);
            }
            setSliderPosition(pos);
          }, 20);
        };
        
        slideLeft();
      };
      
      animate();
    }
  }, [isInView, hasAnimated]);

  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setSliderPosition(percentage);
    },
    []
  );

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    },
    [isDragging, handleMove]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      handleMove(e.touches[0].clientX);
    },
    [handleMove]
  );

  const handleClick = (e: React.MouseEvent) => {
    handleMove(e.clientX);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-square rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-glow"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onClick={handleClick}
    >
      {/* After Image (Background) */}
      <img
        src={skinAfter}
        alt="After skincare transformation"
        width={604}
        height={604}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />

      {/* Before Image (Clipped) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img
          src={skinBefore}
          alt="Before skincare transformation"
          width={604}
          height={604}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/60 text-white text-sm font-medium px-3 py-1.5 rounded-full backdrop-blur-sm">
        Before
      </div>
      <div className="absolute top-4 right-4 bg-primary/90 text-primary-foreground text-sm font-medium px-3 py-1.5 rounded-full backdrop-blur-sm">
        After
      </div>

      {/* Slider Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
        style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
        onMouseDown={handleMouseDown}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => setIsDragging(false)}
        onTouchMove={handleTouchMove}
      >
        {/* Handle Circle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center gap-0.5 border-2 border-primary/20">
          <ChevronLeft className="w-4 h-4 text-primary" />
          <ChevronRight className="w-4 h-4 text-primary" />
        </div>

        {/* Vertical Line Glow */}
        <div className="absolute inset-0 bg-white/50 blur-sm" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute -top-4 -right-4 w-24 h-24 bg-blush rounded-full blur-2xl opacity-60 pointer-events-none" />
      <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/30 rounded-full blur-3xl pointer-events-none" />
    </div>
  );
}
